import { Box, IconButton, ListItem as MuiListItem, ListItemText, Typography, Stack, Avatar } from "@mui/material";
import { ForwardingIcon, LinkIcon, NavigateCircleIcon } from "components/icons";
import { DownloadOutlined } from "@mui/icons-material";
import styled from "@emotion/styled";
import { AttachmentUtil, isImage } from "utils";
import { useAlertContext } from "components/context/AlertContext";
import withForwardMessage from "components/hoc/withForwardMessage";
import FileAvatar from "components/avatar/FileAvatar";
import { SystemConstant } from "const";
import { AvatarGroup, ListItemByDate } from "components";
import { useDownloadFile } from "hooks";
import { palette } from "theme/material";

export const ListFileByDate = ({
  date,
  list,
  isShowGroupName = false,
  onJump2Message,
  onClickMedia,
  ...otherProps
}) => {
  const { showAlert } = useAlertContext();

  return (
    <ListItemByDate date={date} {...otherProps}>
      {list.map((item, index) => (
        <FileItem
          key={`item-${item.sourceMessageId}-${item.id}-${index}`}
          fileItem={item}
          isShowGroupName={isShowGroupName}
          onShowAlert={showAlert}
          onJump2Message={onJump2Message}
          onClickMedia={onClickMedia}
        />
      ))}
    </ListItemByDate>
  );
};

const FileItem = withForwardMessage(({ fileItem, isShowGroupName, onForward, onJump2Message, onClickMedia }) => {
  const { downloadFile } = useDownloadFile();

  const handleDownload = event => {
    event.stopPropagation();

    downloadFile([fileItem.metaData], fileItem.groupId);
  };

  const handleJump2Message = event => {
    event.stopPropagation();

    onJump2Message(fileItem.sourceMessageId);
  };

  const handleForward = event => {
    event.stopPropagation();
    onForward({ attachmentId: fileItem.fileId, sourceMessageId: fileItem.sourceMessageId });
  };

  const handleClickMedia = () => {
    if (isImage(fileItem.metaData.mimeType) && onClickMedia) onClickMedia(fileItem.metaData);
  };

  const isLink = fileItem.fileType === SystemConstant.FILE_TYPE.LINK;

  return (
    <ListItem key={`item-${fileItem.id}`} onClick={handleClickMedia}>
      {isLink ? (
        <Avatar sx={{ width: 48, height: 48, marginRight: 1, bgcolor: palette.grey[200] }}>
          <LinkIcon />
        </Avatar>
      ) : (
        <FileAvatar url={AttachmentUtil.getLocalPath(fileItem.url, fileItem.fileName)} file={fileItem.metaData} />
      )}
      <ListItemText
        disableTypography
        primary={
          <Typography variant="subtitle1" className="ellipsis">
            {fileItem.content}
          </Typography>
        }
        secondary={
          <>
            <Typography component="span" variant="body2" fontWeight={600} className="ellipsis">
              {fileItem.description}
            </Typography>
            {fileItem.groupName && isShowGroupName && (
              <SharingGroupItem
                groupType={fileItem.groupType}
                groupName={fileItem.groupName}
                privateF={fileItem.privateF}
              />
            )}
          </>
        }
      />
      <Box className="icon-list">
        {onJump2Message && (
          <IconButton title="Chuyển tới tin nhắn gốc" aria-label="jump-to-message" onClick={handleJump2Message}>
            <NavigateCircleIcon />
          </IconButton>
        )}
        <IconButton aria-label="forward" title="Chuyển tiếp" onClick={handleForward}>
          <ForwardingIcon />
        </IconButton>

        {fileItem.fileId && (
          <IconButton title="Tải về" edge="end" aria-label="download" onClick={handleDownload}>
            <DownloadOutlined />
          </IconButton>
        )}
      </Box>
    </ListItem>
  );
});

const SharingGroupItem = ({ groupType, groupName, privateF }) => {
  const isShowIcon = [SystemConstant.GROUP_CHAT_TYPE.group, SystemConstant.GROUP_CHAT_TYPE.channel].includes(groupType);

  return (
    <Stack direction="row" className="align-items">
      {isShowIcon && (
        <CustomAvatarGroup group={{ groupType, groupName, privateF }} sx={{ bgcolor: "unset", color: "#707070" }} />
      )}
      <Typography component="span" variant="body2" fontWeight={600} className="ellipsis">
        {groupName}
      </Typography>
    </Stack>
  );
};

const ListItem = styled(MuiListItem)(props => ({
  background: props.theme.palette.grey[100],
  borderRadius: 16,
  "&, & span, & p": { color: props.theme.palette.black },

  "& .icon-list": {
    display: "none",
    "& svg": { color: props.theme.palette.black },
  },

  "&:hover": {
    cursor: "pointer",

    ".icon-list": {
      display: "flex",
      flexDirection: "row",
    },
  },
}));

const CustomAvatarGroup = styled(AvatarGroup)({
  width: 24,
  height: 24,

  "& svg": {
    fontSize: "1rem",
  },
});
